"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  Bold,
  Code,
  Code2,
  CornerDownLeft,
  Heading,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Pilcrow,
  Plus,
  Quote,
  Redo2,
  Sigma,
  SquareFunction,
  Strikethrough,
  Table,
  Type,
  Underline,
  Undo2,
  Workflow,
} from "lucide-react";
import { classNames } from "../ui/class-names";
import { IconButton } from "../ui/icon-button";
import type { EditorCommand } from "./types";

export function ToolbarDivider({
  compact = false,
}: Readonly<{ compact?: boolean }>) {
  return (
    <div
      aria-hidden="true"
      className={classNames(
        "shrink-0 bg-zinc-200",
        compact ? "mx-0.5 h-5 w-px" : "mx-1 h-6 w-px",
      )}
    />
  );
}

export type EditorToolbarMenuItem = Readonly<{
  command: EditorCommand;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}>;

export type EditorToolbarMenuSection = Readonly<{
  label?: string;
  items: EditorToolbarMenuItem[];
}>;

export const editorToolbarHeadingMenuItems: EditorToolbarMenuItem[] = [
  { command: "heading-1", label: "Heading 1", icon: Heading },
  { command: "heading-2", label: "Heading 2", icon: Heading },
  { command: "heading-3", label: "Heading 3", icon: Heading },
  { command: "heading-4", label: "Heading 4", icon: Heading },
  { command: "heading-5", label: "Heading 5", icon: Heading },
  { command: "heading-6", label: "Heading 6", icon: Heading },
  { command: "paragraph", label: "Paragraph", icon: Pilcrow },
];

export const editorToolbarMoreStylesMenuItems: EditorToolbarMenuItem[] = [
  { command: "highlight", label: "Highlight", icon: Highlighter },
];

export const editorToolbarCodeMenuItems: EditorToolbarMenuItem[] = [
  { command: "fenced-code", label: "Code block", icon: Code2 },
  { command: "mermaid", label: "Mermaid", icon: Workflow },
];

export const editorToolbarMediumInsertSections: EditorToolbarMenuSection[] = [
  {
    label: "Blocks",
    items: [
      { command: "blockquote", label: "Quote", icon: Quote },
      { command: "fenced-code", label: "Code block", icon: Code2 },
      { command: "mermaid", label: "Mermaid", icon: Workflow },
      { command: "horizontal-rule", label: "Horizontal rule", icon: Minus },
      { command: "line-break", label: "Line break", icon: CornerDownLeft },
    ],
  },
  {
    label: "Media",
    items: [
      { command: "image", label: "Image", icon: ImageIcon },
      { command: "table", label: "Table", icon: Table },
    ],
  },
  {
    label: "Advanced",
    items: [
      { command: "math-inline", label: "Inline math", icon: Sigma },
      { command: "math-block", label: "Display math", icon: SquareFunction },
    ],
  },
];

export const editorToolbarNarrowFormatSections: EditorToolbarMenuSection[] = [
  {
    label: "Style",
    items: [
      { command: "underline", label: "Underline", icon: Underline },
      { command: "strikethrough", label: "Strikethrough", icon: Strikethrough },
      { command: "inline-code", label: "Inline code", icon: Code },
      { command: "highlight", label: "Highlight", icon: Highlighter },
    ],
  },
];

export const editorToolbarNarrowInsertSections: EditorToolbarMenuSection[] = [
  {
    label: "Lists",
    items: [
      { command: "unordered-list", label: "Bullet list", icon: List },
      { command: "ordered-list", label: "Numbered list", icon: ListOrdered },
      { command: "checklist", label: "Checklist", icon: ListChecks },
    ],
  },
  {
    label: "Blocks",
    items: [
      { command: "blockquote", label: "Quote", icon: Quote },
      { command: "fenced-code", label: "Code block", icon: Code2 },
      { command: "mermaid", label: "Mermaid", icon: Workflow },
      { command: "horizontal-rule", label: "Horizontal rule", icon: Minus },
      { command: "line-break", label: "Line break", icon: CornerDownLeft },
    ],
  },
  {
    label: "Media",
    items: [
      { command: "link", label: "Link", icon: Link },
      { command: "image", label: "Image", icon: ImageIcon },
      { command: "table", label: "Table", icon: Table },
    ],
  },
  {
    label: "Advanced",
    items: [
      { command: "math-inline", label: "Inline math", icon: Sigma },
      { command: "math-block", label: "Display math", icon: SquareFunction },
    ],
  },
];

export type EditorToolbarDropdownMenuProps = Readonly<{
  sections: EditorToolbarMenuSection[];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onCommand: (command: EditorCommand) => void;
  compact?: boolean;
}>;

function flattenSections(sections: EditorToolbarMenuSection[]) {
  return sections.flatMap((section) => section.items);
}

export function EditorToolbarDropdownMenu({
  sections,
  label,
  icon: MenuIcon,
  onCommand,
  compact = false,
}: EditorToolbarDropdownMenuProps) {
  const items = flattenSections(sections);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function getMenuStyle(): CSSProperties | null {
    const button = buttonRef.current;

    if (!button) {
      return null;
    }

    const rect = button.getBoundingClientRect();
    const menuWidth = 208; // w-52
    const gutter = 8;
    const left = Math.min(
      rect.left,
      window.innerWidth - menuWidth - gutter,
    );

    return {
      position: "fixed",
      top: rect.bottom + 4,
      left: Math.max(gutter, left),
    };
  }

  function openMenu() {
    setMenuStyle(getMenuStyle());
    setIsOpen(true);
    setActiveIndex(0);
  }

  function closeMenu(returnFocus = true) {
    setIsOpen(false);
    setMenuStyle(null);

    if (returnFocus) {
      buttonRef.current?.focus();
    }
  }

  function selectItem(index: number) {
    const item = items[index];

    if (item) {
      onCommand(item.command);
    }

    closeMenu();
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      itemRefs.current = [];
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        menuRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }

      closeMenu(false);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
      }
    }

    function repositionMenu(event?: Event) {
      const target = event?.target;
      if (
        target instanceof Node &&
        menuRef.current &&
        (target === menuRef.current || menuRef.current.contains(target))
      ) {
        return;
      }

      setMenuStyle(getMenuStyle());
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    // Capture scroll from any ancestor so the portaled menu stays on the trigger.
    window.addEventListener("scroll", repositionMenu, true);
    window.addEventListener("resize", repositionMenu);
    window.visualViewport?.addEventListener("resize", repositionMenu);
    window.visualViewport?.addEventListener("scroll", repositionMenu);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", repositionMenu, true);
      window.removeEventListener("resize", repositionMenu);
      window.visualViewport?.removeEventListener("resize", repositionMenu);
      window.visualViewport?.removeEventListener("scroll", repositionMenu);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  function handleButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!isOpen) {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown"
      ) {
        event.preventDefault();
        openMenu();
      }

      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, items.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(items.length - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectItem(activeIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
    }
  }

  const menuId = `editor-toolbar-menu-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const sectionStartIndices = sections.map((_, index) =>
    sections
      .slice(0, index)
      .reduce((total, section) => total + section.items.length, 0),
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        aria-label={label}
        title={label}
        onClick={() => {
          if (isOpen) {
            closeMenu(false);
          } else {
            openMenu();
          }
        }}
        onKeyDown={handleButtonKeyDown}
        className={classNames(
          "inline-flex shrink-0 items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          compact ? "size-8" : "size-10",
          isOpen
            ? "bg-zinc-100 text-zinc-950"
            : "text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950",
        )}
      >
        <MenuIcon aria-hidden="true" className="size-4" />
      </button>

      {isOpen && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={label}
              className="z-50 max-h-[min(24rem,70vh)] w-52 overflow-y-auto rounded-md border border-zinc-200 bg-white py-1 shadow-lg"
              style={menuStyle}
            >
              {sections.map((section, sectionIndex) => {
                const sectionStartIndex = sectionStartIndices[sectionIndex] ?? 0;

                return (
                  <div
                    key={section.label ?? `section-${sectionIndex}`}
                    role={section.label ? "group" : undefined}
                    aria-label={section.label}
                  >
                    {section.label ? (
                      <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                        {section.label}
                      </div>
                    ) : null}
                    {section.items.map((item, offset) => {
                      const index = sectionStartIndex + offset;
                      const Icon = item.icon;

                      return (
                        <button
                          key={`${section.label ?? sectionIndex}-${item.command}`}
                          ref={(element) => {
                            itemRefs.current[index] = element;
                          }}
                          type="button"
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => selectItem(index)}
                          onMouseEnter={() => setActiveIndex(index)}
                          className={classNames(
                            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none focus-visible:bg-zinc-100",
                            index === activeIndex
                              ? "bg-zinc-100 text-zinc-950"
                              : "text-zinc-700",
                          )}
                        >
                          <Icon aria-hidden="true" className="size-4 shrink-0" />
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// Thresholds are for the full workspace toolbar width (editor + export).
// Measuring the flex-1 editor strip alone feedback-loops: compact export
// widens the editor, which flips density back, which expands export again.
// Wide (≥1650) expands image/table/math to icons; below that, medium collapses
// those into menus so icons are not clipped.
export const editorToolbarWideThreshold = 1650;
export const editorToolbarMediumThreshold = 900;

export type ToolbarDensity = "wide" | "medium" | "narrow";

export function useToolbarCompact() {
  const containerRef = useRef<HTMLDivElement>(null);
  // null until measured so SSR/hydration never paint the wrong export control
  // (wide→narrow on phones, or narrow→wide on desktop).
  const [density, setDensity] = useState<ToolbarDensity | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const update = (entries?: ResizeObserverEntry[]) => {
      const width = entries?.[0]?.contentRect.width ?? container.clientWidth;

      // Ignore unmeasured layouts (common in jsdom before stubbing).
      if (width <= 0) {
        return;
      }

      if (width < editorToolbarMediumThreshold) {
        setDensity("narrow");
      } else if (width < editorToolbarWideThreshold) {
        setDensity("medium");
      } else {
        setDensity("wide");
      }
    };

    update();

    const ResizeObserverConstructor = globalThis.ResizeObserver;

    if (!ResizeObserverConstructor) {
      const onResize = () => update();
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }

    const observer = new ResizeObserverConstructor(update);
    observer.observe(container);
    const onResize = () => update();
    window.addEventListener("resize", onResize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return { containerRef, density };
}

function MenuFromItems({
  items,
  label,
  icon,
  onCommand,
  compact = false,
}: Readonly<{
  items: EditorToolbarMenuItem[];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onCommand: (command: EditorCommand) => void;
  compact?: boolean;
}>) {
  return (
    <EditorToolbarDropdownMenu
      sections={[{ items }]}
      label={label}
      icon={icon}
      onCommand={onCommand}
      compact={compact}
    />
  );
}

function ToolbarGroup({
  label,
  children,
}: Readonly<{
  label: string;
  children: ReactNode;
}>) {
  return (
    <div className="flex shrink-0 items-center" aria-label={label}>
      {children}
    </div>
  );
}

export function EditorToolbar({
  canRedo,
  canUndo,
  onCommand,
  onRedo,
  onUndo,
  density,
}: Readonly<{
  canRedo: boolean;
  canUndo: boolean;
  onCommand: (command: EditorCommand) => void;
  onRedo: () => void;
  onUndo: () => void;
  density: ToolbarDensity;
}>) {
  const isWide = density === "wide";
  const isMedium = density === "medium";
  const isNarrow = density === "narrow";
  const compact = isNarrow;

  return (
    <div
      role="toolbar"
      aria-label="Markdown formatting toolbar"
      className={classNames(
        "flex h-full min-w-0 flex-1 items-center overflow-x-auto scrollbar-hidden",
        // Match ToolbarDivider side margins so history isn't flush to the gutter
        // border on the left while still padded before the next separator.
        compact ? "px-0.5" : "pl-1 pr-2 sm:pr-3",
      )}
    >
      {/* w-max keeps icons packed; prevents flex from spreading gaps across free space.
          Spacing between groups comes only from ToolbarDivider margins (avoid stacking
          flex gap + divider mx, which left a wide pocket around undo/redo). */}
      <div className="flex w-max items-center">
      <ToolbarGroup label="History controls">
        <IconButton
          label="Undo"
          compact={compact}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <Undo2 aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label="Redo"
          compact={compact}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <Redo2 aria-hidden="true" className="size-4" />
        </IconButton>
      </ToolbarGroup>

      <ToolbarDivider compact={compact} />

      {isNarrow ? (
        <>
          <ToolbarGroup label="Inline formatting controls">
            <IconButton
              label="Bold"
              compact={compact}
              onClick={() => onCommand("bold")}
            >
              <Bold aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label="Italic"
              compact={compact}
              onClick={() => onCommand("italic")}
            >
              <Italic aria-hidden="true" className="size-4" />
            </IconButton>
          </ToolbarGroup>

          <ToolbarDivider compact={compact} />

          <ToolbarGroup label="Block formatting controls">
            <MenuFromItems
              items={editorToolbarHeadingMenuItems}
              label="Heading"
              icon={Heading}
              onCommand={onCommand}
              compact={compact}
            />
          </ToolbarGroup>

          <ToolbarDivider compact={compact} />

          <EditorToolbarDropdownMenu
            sections={editorToolbarNarrowFormatSections}
            label="Format"
            icon={Type}
            onCommand={onCommand}
            compact={compact}
          />
          <EditorToolbarDropdownMenu
            sections={editorToolbarNarrowInsertSections}
            label="Insert"
            icon={Plus}
            onCommand={onCommand}
            compact={compact}
          />
        </>
      ) : null}

      {isMedium || isWide ? (
        <>
          <ToolbarGroup label="Inline formatting controls">
            <IconButton label="Bold" onClick={() => onCommand("bold")}>
              <Bold aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton label="Italic" onClick={() => onCommand("italic")}>
              <Italic aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton label="Underline" onClick={() => onCommand("underline")}>
              <Underline aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label="Strikethrough"
              onClick={() => onCommand("strikethrough")}
            >
              <Strikethrough aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label="Inline code"
              onClick={() => onCommand("inline-code")}
            >
              <Code aria-hidden="true" className="size-4" />
            </IconButton>
            <MenuFromItems
              items={editorToolbarMoreStylesMenuItems}
              label="More styles"
              icon={Highlighter}
              onCommand={onCommand}
            />
          </ToolbarGroup>

          <ToolbarDivider />

          <ToolbarGroup label="Block formatting controls">
            <MenuFromItems
              items={editorToolbarHeadingMenuItems}
              label="Heading"
              icon={Heading}
              onCommand={onCommand}
            />
            {isWide ? (
              <>
                <IconButton
                  label="Quote"
                  onClick={() => onCommand("blockquote")}
                >
                  <Quote aria-hidden="true" className="size-4" />
                </IconButton>
                <MenuFromItems
                  items={editorToolbarCodeMenuItems}
                  label="Code"
                  icon={Code2}
                  onCommand={onCommand}
                />
              </>
            ) : null}
            <IconButton
              label="Bullet list"
              onClick={() => onCommand("unordered-list")}
            >
              <List aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label="Numbered list"
              onClick={() => onCommand("ordered-list")}
            >
              <ListOrdered aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label="Checklist"
              onClick={() => onCommand("checklist")}
            >
              <ListChecks aria-hidden="true" className="size-4" />
            </IconButton>
            {isWide ? (
              <>
                <IconButton
                  label="Horizontal rule"
                  onClick={() => onCommand("horizontal-rule")}
                >
                  <Minus aria-hidden="true" className="size-4" />
                </IconButton>
                <IconButton
                  label="Line break"
                  onClick={() => onCommand("line-break")}
                >
                  <CornerDownLeft aria-hidden="true" className="size-4" />
                </IconButton>
              </>
            ) : null}
          </ToolbarGroup>

          <ToolbarDivider />

          <ToolbarGroup label="Insert controls">
            <IconButton label="Link" onClick={() => onCommand("link")}>
              <Link aria-hidden="true" className="size-4" />
            </IconButton>
            {isWide ? (
              <>
                <IconButton label="Image" onClick={() => onCommand("image")}>
                  <ImageIcon aria-hidden="true" className="size-4" />
                </IconButton>
                <IconButton label="Table" onClick={() => onCommand("table")}>
                  <Table aria-hidden="true" className="size-4" />
                </IconButton>
                <IconButton
                  label="Inline math"
                  onClick={() => onCommand("math-inline")}
                >
                  <Sigma aria-hidden="true" className="size-4" />
                </IconButton>
                <IconButton
                  label="Display math"
                  onClick={() => onCommand("math-block")}
                >
                  <SquareFunction aria-hidden="true" className="size-4" />
                </IconButton>
              </>
            ) : (
              <EditorToolbarDropdownMenu
                sections={editorToolbarMediumInsertSections}
                label="Insert"
                icon={Plus}
                onCommand={onCommand}
              />
            )}
          </ToolbarGroup>
        </>
      ) : null}
      </div>
    </div>
  );
}
